import { supabase } from '@/lib/supabase';
import { SignInWithApple } from '@capacitor-community/apple-sign-in';
import { Capacitor } from '@capacitor/core';
import { sha256Hex } from '@/lib/sha256';

/**
 * Generate a random string for nonce
 */
function generateNonce(length: number = 32): string {
    const charset = '0123456789ABCDEFGHIJKLMNOPQRSTUVXYZabcdefghijklmnopqrstuvwxyz-._';
    let result = '';
    const randomValues = new Uint8Array(length);
    crypto.getRandomValues(randomValues);
    randomValues.forEach((value) => {
        result += charset[value % charset.length];
    });
    return result;
}

/**
 * SHA-256 hash function. Uses crypto.subtle when available, with a pure-JS
 * fallback for native WebViews that don't expose it (see @/lib/sha256).
 */
async function sha256(str: string): Promise<string> {
    return sha256Hex(str);
}

export const appleAuthService = {
    /**
     * Sign in with Apple using native API (iOS) or web OAuth (web)
     * This implementation uses the native Apple Sign In API which keeps users in-app
     * and satisfies Apple Review Guideline 4.0
     */
    async signInWithApple(): Promise<void> {
        try {
            const isNative = Capacitor.isNativePlatform();

            if (isNative) {
                await this.signInWithAppleNative();
            } else {
                await this.signInWithAppleWeb();
            }
        } catch (error) {
            console.error('❌ [Apple Auth] Sign in error:', error);
            throw error;
        }
    },

    /**
     * Sign in with Apple using native iOS API
     * Uses ASAuthorizationAppleIDProvider - stays 100% in-app
     */
    async signInWithAppleNative(): Promise<void> {
        try {
            console.log('🍎 [Apple Auth Native] Starting native Apple Sign In');

            // Generate nonce for security
            const rawNonce = generateNonce();
            const hashedNonce = await sha256(rawNonce);

            console.log('🍎 [Apple Auth Native] Nonce generated');

            // Call native Apple Sign In API
            const result = await SignInWithApple.authorize({
                clientId: 'com.withharmonia.app.signin',
                redirectURI: 'https://jvlujbqgxdeloslojxnj.supabase.co/auth/v1/callback',
                scopes: 'email name',
                state: hashedNonce,
                nonce: hashedNonce
            });

            console.log('🍎 [Apple Auth Native] Authorization successful');
            console.log('🍎 [Apple Auth Native] Response received:', JSON.stringify(result));

            if (!result.response?.identityToken) {
                throw new Error('No identity token received from Apple');
            }

            console.log('🍎 [Apple Auth Native] Identity token received');

            // Wait for network to stabilize after modal dismissal
            console.log('⏳ [Apple Auth Native] Waiting for network to stabilize...');
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Sign in to Supabase using the Apple identity token with retry logic
            const identityToken = result.response.identityToken;
            let retries = 3;
            let lastError: any;
            let authData: any;

            while (retries > 0) {
                try {
                    console.log(`🍎 [Apple Auth Native] Attempting Supabase sign in (attempt ${4 - retries}/3)...`);

                    const supabaseResult = await supabase.auth.signInWithIdToken({
                        provider: 'apple',
                        token: identityToken,
                        nonce: rawNonce
                    });

                    if (supabaseResult.error) {
                        lastError = supabaseResult.error;
                        console.error(`❌ [Apple Auth Native] Supabase sign in error (attempt ${4 - retries}/3):`, supabaseResult.error);

                        // Retry on network errors
                        if (supabaseResult.error.name?.includes('Fetch') || supabaseResult.error.status === 0) {
                            retries--;
                            if (retries > 0) {
                                console.log(`🔄 [Apple Auth Native] Retrying in 2 seconds... (${retries} attempts left)`);
                                await new Promise(resolve => setTimeout(resolve, 2000));
                                continue;
                            }
                        }
                        throw supabaseResult.error;
                    }

                    authData = supabaseResult.data;
                    console.log('✅ [Apple Auth Native] Successfully signed in to Supabase');
                    console.log('🍎 [Apple Auth Native] User ID:', authData.user?.id);
                    console.log('🍎 [Apple Auth Native] User email:', authData.user?.email);
                    
                    // Stocker temporairement le provider OAuth pour le router guard
                    if (authData.user?.app_metadata?.provider) {
                        localStorage.setItem('harmonia_oauth_provider', authData.user.app_metadata.provider);
                        console.log('💾 [Apple Auth Native] Stored OAuth provider:', authData.user.app_metadata.provider);
                    }
                    
                    break;
                } catch (err) {
                    lastError = err;
                    retries--;
                    if (retries > 0) {
                        console.log(`🔄 [Apple Auth Native] Retrying in 2 seconds... (${retries} attempts left)`);
                        await new Promise(resolve => setTimeout(resolve, 2000));
                    }
                }
            }

            // If we exhausted all retries, throw the last error
            if (retries === 0 && lastError) {
                console.error('❌ [Apple Auth Native] All retry attempts failed');
                throw lastError;
            }

        } catch (error: any) {
            console.error('❌ [Apple Auth Native] Error:', error);

            // Handle user cancellation gracefully
            if (error.message?.includes('User cancelled') || error.code === '1001') {
                console.log('ℹ️ [Apple Auth Native] User cancelled sign in');
                throw new Error('User cancelled sign in');
            }

            throw error;
        }
    },

    /**
     * Sign in with Apple using web OAuth flow
     * Used when running in browser (not native app)
     */
    async signInWithAppleWeb(): Promise<void> {
        try {
            console.log('🍎 [Apple Auth Web] Starting web OAuth flow');

            const redirectUrl = `${window.location.origin}/auth/callback`;

            const { data, error } = await supabase.auth.signInWithOAuth({
                provider: 'apple',
                options: {
                    redirectTo: redirectUrl
                }
            });

            if (error) {
                console.error('❌ [Apple Auth Web] OAuth error:', error);
                throw error;
            }

            if (data.url) {
                console.log('🍎 [Apple Auth Web] Redirecting to Apple OAuth');
                window.location.href = data.url;
            } else {
                throw new Error('No OAuth URL generated');
            }
        } catch (error) {
            console.error('❌ [Apple Auth Web] Error:', error);
            throw error;
        }
    }
};
